import React, { useId } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * v2 form controls — Input / Textarea / Select with label, helper and error
 * states. 1px borders; cobalt focus ring (user action); danger ink on error.
 */
interface FieldShellProps {
  label?: string;
  helper?: string;
  error?: string;
  required?: boolean;
  id: string;
  children: React.ReactNode;
}

const FieldShell: React.FC<FieldShellProps> = ({ label, helper, error, required, id, children }) => (
  <div className="flex flex-col gap-s2">
    {label && (
      <label htmlFor={id} className="text-row-title text-ink">
        {label}
        {required && <span className="ml-s1 text-danger-ink">*</span>}
      </label>
    )}
    {children}
    {error ? (
      <p className="text-meta text-danger-ink">{error}</p>
    ) : helper ? (
      <p className="text-meta text-ink-muted">{helper}</p>
    ) : null}
  </div>
);

// `max-md:text-[16px]`: mobile Safari zooms the page in whenever a field
// smaller than 16px takes focus, and never zooms back out — the user is
// left scrolling a form sideways. The one size it leaves alone is 16.
const controlBase =
  'w-full rounded-control border bg-surface text-body-md max-md:text-[16px] text-ink placeholder:text-ink-muted ' +
  'transition-colors duration-100 focus:outline-none focus:ring-2 focus:ring-offset-0 disabled:opacity-50 disabled:bg-canvas';

const controlState = (error?: string) =>
  error
    ? 'border-danger-border focus:border-danger-ink focus:ring-danger-border'
    : 'border-line hover:border-line-strong focus:border-action focus:ring-action-border';

// Spreadsheet cell: no frame until hovered or focused, so a row of inputs
// reads as a row of values. Edited = cobalt tint (the user's action),
// invalid = danger tint; the message rides on the title, not under the cell.
const cellBase =
  'h-7 w-full min-w-0 rounded-control border px-s2 text-body-md max-md:text-[16px] text-ink placeholder:text-ink-muted ' +
  'transition-colors duration-100 focus:outline-none disabled:opacity-50';

const cellState = (error?: string, dirty?: boolean) =>
  error
    ? 'border-danger-border bg-danger-bg focus:border-danger-ink'
    : dirty
      ? 'border-action-border bg-action-bg focus:border-action'
      : 'border-transparent bg-transparent hover:border-line focus:border-action focus:bg-surface';

type NativeInput = React.InputHTMLAttributes<HTMLInputElement>;
export interface InputProps extends NativeInput {
  label?: string;
  helper?: string;
  error?: string;
  /** Table-cell input: compact, frameless until hover/focus, no label or helper row. */
  cell?: boolean;
  /** Cell only — the value differs from what is saved. */
  dirty?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ label, helper, error, required, className = '', id, cell, dirty, ...rest }, ref) => {
    const autoId = useId();
    const fieldId = id || autoId;
    if (cell) {
      return (
        <input
          ref={ref}
          id={fieldId}
          required={required}
          aria-invalid={!!error}
          title={error}
          className={`${cellBase} ${cellState(error, dirty)} ${className}`}
          {...rest}
        />
      );
    }
    return (
      <FieldShell label={label} helper={helper} error={error} required={required} id={fieldId}>
        <input
          ref={ref}
          id={fieldId}
          required={required}
          aria-invalid={!!error}
          className={`${controlBase} ${controlState(error)} h-9 px-s4 ${className}`}
          {...rest}
        />
      </FieldShell>
    );
  }
);
Input.displayName = 'Input';

type NativeTextarea = React.TextareaHTMLAttributes<HTMLTextAreaElement>;
export interface TextareaProps extends NativeTextarea {
  label?: string;
  helper?: string;
  error?: string;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, helper, error, required, className = '', id, rows = 3, ...rest }, ref) => {
    const autoId = useId();
    const fieldId = id || autoId;
    return (
      <FieldShell label={label} helper={helper} error={error} required={required} id={fieldId}>
        <textarea
          ref={ref}
          id={fieldId}
          rows={rows}
          required={required}
          aria-invalid={!!error}
          className={`${controlBase} ${controlState(error)} px-s4 py-s3 resize-y ${className}`}
          {...rest}
        />
      </FieldShell>
    );
  }
);
Textarea.displayName = 'Textarea';

type NativeSelect = React.SelectHTMLAttributes<HTMLSelectElement>;
export interface SelectProps extends NativeSelect {
  label?: string;
  helper?: string;
  error?: string;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, helper, error, required, className = '', id, options, placeholder, ...rest }, ref) => {
    const autoId = useId();
    const fieldId = id || autoId;
    return (
      <FieldShell label={label} helper={helper} error={error} required={required} id={fieldId}>
        <div className="relative">
          <select
            ref={ref}
            id={fieldId}
            required={required}
            aria-invalid={!!error}
            className={`${controlBase} ${controlState(error)} h-9 pl-s4 pr-s7 appearance-none ${className}`}
            {...rest}
          >
            {placeholder && <option value="">{placeholder}</option>}
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-s3 top-1/2 h-[13px] w-[13px] -translate-y-1/2 text-ink-muted" />
        </div>
      </FieldShell>
    );
  }
);
Select.displayName = 'Select';
